import React from 'react';
import {
  View, Text, StyleSheet, Image, Pressable, ViewStyle,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { FontAwesome } from '@expo/vector-icons';
import { AssetCustomField, AssetInstance, TemplateType } from '../../../../../constants/Types';
import { AppScreen } from '../../../../../navigation/LinkingConfiguration';
import Colors from '../../../../../constants/Colors';
import isMobile from '../../../../../components/IsMobile';
import Style from '../../../../../constants/Style';
import { assetLocationName } from '../../../Utils';
import PressableStyled, { PressableState } from '../../../../../components/PressableStyled';
import FloorIcon from '../../../../../components/16pxIcons/FloorIcon';
import GearIcon16 from '../../../../../components/16pxIcons/GearIcon16';
import LocationIcon16 from '../../../../../components/16pxIcons/LocationIcon16';

const placeholderImage = require('../../../../../assets/images/image_placeholder.png');

interface AssetListItemProps {
  asset: AssetInstance,
  onPress: () => void,
  style: ViewStyle | ViewStyle[],
  imageShown: boolean,
  idShown: boolean,
  templateShown: boolean,
  locationShown: boolean,
  modelShown: boolean,
  manufacturerShown: boolean,
  serialShown: boolean,
}

const AssetListItem = ({
  asset, onPress, style, imageShown, idShown, templateShown, locationShown, manufacturerShown,
  modelShown, serialShown,
}: AssetListItemProps) => {
  const navigation = useNavigation();
  const isDev: boolean = useSelector((state:
    { devState: { isDev: boolean } }) => state.devState.isDev);
  const locationName: string = assetLocationName(asset);

  const mapLocationSpecified: boolean = asset.customFields
    .some((field: AssetCustomField) => field.name.toLowerCase() === 'geox' && field.value)
    && asset.customFields
      .some((field: AssetCustomField) => field.name.toLowerCase() === 'geoy' && field.value);

  const goToTemplate: () => void = () => {
    navigation.navigate(
      isDev ? AppScreen.TEMPLATE_MANAGEMENTV2 : AppScreen.TEMPLATE_MANAGEMENT as any,
      { templateId: asset.templateId },
    );
  };

  const goToMap: () => void = () => {
    navigation.navigate(AppScreen.MAP as any, {
      floor: asset.locationFloor,
      area: asset.locationArea,
      space: asset.locationSpace,
      locationId: asset?.locationId,
      assetsView: asset.type === TemplateType.ASSET,
      sensorsView: asset.type === TemplateType.SENSOR,
    });
  };

  return <Pressable
    style={({ hovered, pressed }: PressableState) => [
      styles.container, style, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
    ]}
    {...{ onPress }}
  >
    {isMobile ? <>
      <View style={styles.column}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.label, styles.bold]}>{asset.name}</Text>
          <Text style={[styles.label, styles.highlight]}>{asset.id}</Text>
        </View>
        <FontAwesome
          name='angle-right'
          size={24}
          color={styles.highlight.color}
          style={styles.icon}
        />
      </View>
      <View>
        <View style={styles.column}>
          <GearIcon16
            fill={styles.container.borderColor}
            style={styles.icon}
          />
          <Text style={styles.label} ellipsizeMode='tail'>{asset.templateName}</Text>
        </View>
        <View style={styles.column}>
          <FloorIcon
            fill={styles.container.borderColor}
            style={styles.icon}
          />
          <Text style={styles.label}>{locationName}</Text>
        </View>
        <View style={styles.column}>
          <LocationIcon16
            fill={mapLocationSpecified
              ? Colors.light.hereworksGreen500 : Colors.light.hereworksBlack100}
            style={styles.icon}
          />
          <Text style={styles.label}>
            {mapLocationSpecified ? 'Location specified' : 'Not located'}
            {' on the map'}
          </Text>
        </View>
      </View>
    </> : <>
      <View style={styles.column}>
        {imageShown && <Image
          style={styles.image}
          source={asset.imageUrl ? { uri: asset.imageUrl } : undefined}
          defaultSource={placeholderImage}
        />}
        <Text style={styles.label}>{asset.name}</Text>
      </View>
      {idShown && <Text style={[styles.column, styles.label]}>{asset.id}</Text>}
      {templateShown && <View style={styles.column}>
        <PressableStyled
          onPress={goToTemplate}
          style={{ maxWidth: '100%' }}
        >
          <Text
            style={[styles.label, styles.highlight]}
            ellipsizeMode='tail'
          >
            {asset.templateName}
          </Text>
        </PressableStyled>
      </View>}
      {locationShown && <View style={styles.column}>
        <PressableStyled
          onPress={goToMap}
          style={{ maxWidth: '100%' }}
        >
          <Text
            style={[styles.label, styles.highlight]}
            ellipsizeMode='tail'
          >
            {locationName}
          </Text>
        </PressableStyled>
      </View>}
      {manufacturerShown
        && <Text style={[styles.column, styles.label]}>{asset.manufacturer || '-'}</Text>}
      {modelShown
        && <Text style={[styles.column, styles.label]}>{asset.model || '-'}</Text>}
      {serialShown
        && <Text style={[styles.column, styles.label]}>{asset.serial || '-'}</Text>}
    </>}
  </Pressable>;
};

const styles = StyleSheet.create({
  container: isMobile ? {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    paddingVertical: 12,
    backgroundColor: '#FFF',
    justifyContent: 'space-between',
  } : {
    ...Style.row,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue200,
  },
  icon: {
    marginEnd: 8,
  },
  image: {
    width: 64,
    height: isMobile ? 48 : 44,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: Colors.light.hereworksPrimaryGrey200,
    marginEnd: 16,
  },
  label: {
    fontSize: isMobile ? 12 : 14,
    lineHeight: isMobile ? 14 : 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0,
  },
  bold: {
    fontSize: 14,
    lineHeight: 16,
    fontFamily: 'Poppins_600SemiBold',
  },
  highlight: {
    color: Colors.light.hereworksBlue500,
    textDecorationLine: isMobile ? undefined : 'underline',
  },
  column: {
    flex: isMobile ? undefined : 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginVertical: isMobile ? 4 : undefined,
  },
});

export default AssetListItem;
